import { useState, useRef, useMemo, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmtTime, fmtDur, fmtMins, todayNZ, fmtDMY, isoToNZInput, nzInputToISO } from "@/lib/format";
import { Checkbox } from "@/components/ui/checkbox";
import TimelineTrack, { TIMELINE_KINDS } from "@/components/TimelineTrack";
import { GapReasonDialog } from "@/components/GapReasonDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  GanttChartSquare,
  CalendarDays,
  TriangleAlert,
  CheckCircle2,
  Radio,
  Download,
  FileText,
  Pencil,
  Save,
  AlarmClockOff,
  Users,
  Layers,
  Mail,
  Clock,
} from "lucide-react";

// Convert an NZ (Pacific/Auckland) wall time (date + "HH:MM") to a UTC ISO string.
// Delegates to the DST-safe, browser-timezone-independent helper in lib/format.
function nzHmToIso(dateStr, hm) {
  if (!hm) return null;
  return nzInputToISO(`${dateStr}T${hm}`);
}

// ISO string -> "HH:MM" in NZ time for the input default.
function isoToNzHm(iso) {
  if (!iso) return "";
  return isoToNZInput(iso).slice(11, 16);
}

// "HH:MM" -> minutes of day.
function hmToMin(hm) {
  const [h, m] = (hm || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Filename-safe slug from a cleaner's name.
function slugify(name) {
  return (name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
}

export default function Timeline() {
  const qc = useQueryClient();
  const { motel } = useAuth();
  const [date, setDate] = useState(todayNZ());
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [gapCtx, setGapCtx] = useState(null); // { gap, lane }
  const [breakEdit, setBreakEdit] = useState(null); // clicked break bubble to correct
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  const [focusCleaner, setFocusCleaner] = useState("all");
  const [defStart, setDefStart] = useState(() => localStorage.getItem("tl_default_start") || "08:30");
  const [defEnd, setDefEnd] = useState(() => localStorage.getItem("tl_default_end") || "14:30");
  // If a default window is saved, start in that fixed window (persists across reload); else auto-fit.
  const [mode, setMode] = useState(() => (localStorage.getItem("tl_default_start") ? "manual" : "auto"));
  const [manualStart, setManualStart] = useState(defStart);
  const [manualEnd, setManualEnd] = useState(defEnd);
  const [draftStart, setDraftStart] = useState(defStart);
  const [draftEnd, setDraftEnd] = useState(defEnd);
  const captureRef = useRef(null);
  const exportingRef = useRef(false);
  const firstLoad = useRef(true);
  const settingsApplied = useRef(false);
  const isToday = date === todayNZ();

  // Motel-wide Timeline default (shared by all managers). Falls back to the localStorage cache.
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    staleTime: 60000,
  });
  useEffect(() => {
    if (!settings || settingsApplied.current) return;
    settingsApplied.current = true;
    const ds = settings.timeline_default_start || "08:30";
    const de = settings.timeline_default_end || "14:30";
    localStorage.setItem("tl_default_start", ds);
    localStorage.setItem("tl_default_end", de);
    setDefStart(ds); setDefEnd(de);
    setManualStart(ds); setManualEnd(de);
    setDraftStart(ds); setDraftEnd(de);
    setMode("manual"); // a motel default always exists → show it on load
  }, [settings]);

  // Payroll email (prefills the "email to payroll" recipient).
  const { data: payrollSettings } = useQuery({
    queryKey: ["payroll-settings"],
    queryFn: async () => (await api.get("/payroll-settings")).data,
    staleTime: 60000,
  });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  useEffect(() => {
    if (payrollSettings?.payroll_email && !emailRecipient) setEmailRecipient(payrollSettings.payroll_email);
  }, [payrollSettings]); // eslint-disable-line react-hooks/exhaustive-deps


  const { data, isLoading } = useQuery({
    queryKey: ["timeline", date],
    queryFn: async () => (await api.get(`/timeline?date=${date}`)).data,
    refetchInterval: isToday ? 30000 : false,
  });

  const { data: stuckData } = useQuery({
    queryKey: ["stuck-tasks"],
    queryFn: async () => (await api.get("/stuck-tasks")).data,
    refetchInterval: 60000,
  });
  const stuck = stuckData?.stuck || [];

  const forceEnd = useMutation({
    mutationFn: (s) =>
      api.post(`/cleans/${s.clean_id}/force-end`, {
        cleaner_id: s.cleaner_id,
        reason: "Stuck in progress past shift end",
      }),
    onSuccess: () => {
      toast.success("Task force-ended");
      qc.invalidateQueries({ queryKey: ["stuck-tasks"] });
      qc.invalidateQueries({ queryKey: ["timeline", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const clockOut = useMutation({
    mutationFn: (cleanerId) => api.post("/timeline/clock-out", { date, cleaner_id: cleanerId }),
    onSuccess: (res) => {
      toast.success(`Clocked out at ${res.data.finish}`);
      qc.invalidateQueries({ queryKey: ["timeline", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const clockOutUndo = useMutation({
    mutationFn: (cleanerId) => api.post("/timeline/clock-out/undo", { date, cleaner_id: cleanerId }),
    onSuccess: () => {
      toast.success("Clock-out undone");
      qc.invalidateQueries({ queryKey: ["timeline", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const shiftDay = (delta) => {
    const [y, m, d] = date.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    setDate(dt.toISOString().slice(0, 10));
  };

  // Draw the PDF cover page (title, meta rows, and per-cleaner worked-hours totals).
  const drawCoverPage = (pdf, { subtitle, who, s, e, dayCount, gStart, gEnd, totals }) => {
    const generated = new Date().toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", dateStyle: "medium", timeStyle: "short" });
    pdf.setTextColor(20); pdf.setFontSize(34);
    pdf.text(motel?.name || "KlenaPro", 60, 120);
    pdf.setFontSize(20); pdf.setTextColor(90);
    pdf.text(subtitle, 60, 156);
    pdf.setDrawColor(210); pdf.line(60, 184, 840, 184);
    pdf.setFontSize(14);
    const rows = [
      ["Pay period", `${fmtDMY(s)} – ${fmtDMY(e)}`],
      ["Scope", who],
      ["Days with activity", String(dayCount)],
      ["Timeline scale", `${gStart}–${gEnd}`],
      ["Generated", generated],
    ];
    let y = 224;
    rows.forEach(([k, v]) => {
      pdf.setTextColor(120); pdf.text(k, 60, y);
      pdf.setTextColor(20); pdf.text(v, 280, y);
      y += 34;
    });
    if (totals && totals.length) {
      y += 14;
      pdf.setFontSize(15); pdf.setTextColor(20);
      pdf.text("Worked hours this period", 60, y); y += 10;
      pdf.setDrawColor(225); pdf.line(60, y, 560, y); y += 26;
      pdf.setFontSize(13);
      let grand = 0;
      totals.forEach((t) => {
        grand += t.secs;
        pdf.setTextColor(60); pdf.text(t.name, 60, y);
        pdf.setTextColor(20); pdf.text(fmtDur(t.secs), 420, y);
        y += 24;
      });
      pdf.setDrawColor(225); pdf.line(60, y - 8, 560, y - 8);
      pdf.setTextColor(20); pdf.setFontSize(13);
      pdf.text("Total", 60, y + 8);
      pdf.text(fmtDur(grand), 420, y + 8);
    }
    pdf.setFontSize(10); pdf.setTextColor(150);
    pdf.text("KlenaPro Housekeeping Ops · shift review", 60, 610);
  };

  const exportPdf = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const node = captureRef.current;
      const w = node.scrollWidth;
      const h = node.scrollHeight;
      const dataUrl = await toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: w, height: h });
      const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "px", format: [w + 48, h + 96] });
      pdf.setFontSize(16);
      pdf.text(`Timeline — ${fmtDMY(date)}${focusName ? ` · ${focusName}` : ""}`, 24, 32);
      pdf.setFontSize(10);
      pdf.setTextColor(120);
      pdf.text("KlenaPro shift review", 24, 50);
      pdf.addImage(dataUrl, "PNG", 24, 72, w, h);
      pdf.save(`timeline_${date}${focusName ? `_${slugify(focusName)}` : ""}.pdf`);
    } catch {
      toast.error("Could not export the PDF — please try again.");
    } finally {
      setExporting(false);
    }
  };

  // Min/max minutes-of-day for a given day's lanes (relative to that day).
  const dayMinsRange = (day, lanesArr) => {
    const mins = [];
    (lanesArr || []).forEach((l) => (l.items || []).forEach((it) => {
      [it.start, it.eff_end].forEach((iso) => {
        const [d, t] = isoToNZInput(iso).split("T");
        const [h, mm] = t.split(":").map(Number);
        let m = h * 60 + mm;
        if (d > day) m += 1440; else if (d < day) m -= 1440;
        mins.push(m);
      });
    }));
    return mins;
  };

  // ---- PDF builders (pure build; the wrapper handles the exporting overlay + restore) ----

  // Scoped pay-period PDF (all cleaners, or just the focused one).
  const buildScopedPayPeriod = async (tid) => {
    const scope = (lns) => (focusCleaner === "all" ? (lns || []) : (lns || []).filter((l) => l.cleaner_id === focusCleaner));
    const who = focusName || "All cleaners";
    const period = (await api.get(`/timesheets/period?date=${date}`)).data;
    const s = period.period_start, e = period.period_end;
    if (!s || !e) { toast.error("Could not load the pay period.", { id: tid }); return null; }
    const days = [];
    const [sy, sm, sd] = s.split("-").map(Number);
    const cur = new Date(Date.UTC(sy, sm - 1, sd));
    const [ey, em, ed] = e.split("-").map(Number);
    const endUTC = Date.UTC(ey, em - 1, ed);
    while (cur.getTime() <= endUTC) { days.push(cur.toISOString().slice(0, 10)); cur.setUTCDate(cur.getUTCDate() + 1); }

    const allMins = [];
    const dataDays = [];
    const totals = new Map();
    for (const day of days) {
      const dayData = await qc.ensureQueryData({ queryKey: ["timeline", day], queryFn: async () => (await api.get(`/timeline?date=${day}`)).data });
      const scoped = scope(dayData.lanes);
      const dm = dayMinsRange(day, scoped);
      if (dm.length) { dataDays.push(day); allMins.push(...dm); }
      scoped.forEach((l) => {
        if (!(l.items || []).length) return;
        const cur2 = totals.get(l.cleaner_id) || { name: l.cleaner_name, secs: 0 };
        cur2.secs += l.worked_seconds || 0;
        totals.set(l.cleaner_id, cur2);
      });
    }
    if (!dataDays.length) { toast.info(`No cleaning activity for ${who} in this pay period.`, { id: tid }); return null; }

    const gStart = minToHm(Math.floor(Math.min(...allMins) / 30) * 30);
    const gEnd = minToHm(Math.ceil(Math.max(...allMins) / 30) * 30);
    setManualStart(gStart); setManualEnd(gEnd); setMode("manual");
    setExportProgress({ current: 0, total: dataDays.length });

    const totalsList = [...totals.values()].sort((a, b) => a.name.localeCompare(b.name));
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [900, 640] });
    drawCoverPage(pdf, { subtitle: "Pay-period timeline", who, s, e, dayCount: dataDays.length, gStart, gEnd, totals: totalsList });

    for (let i = 0; i < dataDays.length; i++) {
      const day = dataDays[i];
      setExportProgress({ current: i + 1, total: dataDays.length });
      toast.loading(`Building pay-period PDF… day ${i + 1}/${dataDays.length}`, { id: tid });
      setDate(day);
      await new Promise((r) => setTimeout(r, 650));
      if (!captureRef.current) continue;
      const node = captureRef.current;
      const w = node.scrollWidth, h = node.scrollHeight;
      const dataUrl = await toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: w, height: h });
      pdf.addPage([w + 48, h + 96], "landscape");
      pdf.setFontSize(16); pdf.setTextColor(20);
      pdf.text(`Timeline — ${fmtDMY(day)}${focusName ? ` · ${focusName}` : ""}`, 24, 32);
      pdf.setFontSize(10); pdf.setTextColor(120);
      pdf.text(`KlenaPro shift review · scale ${gStart}–${gEnd}`, 24, 50);
      pdf.addImage(dataUrl, "PNG", 24, 72, w, h);
    }
    const summary = `Pay period: ${fmtDMY(s)} – ${fmtDMY(e)}\nScope: ${who}\n\nWorked hours:\n` +
      totalsList.map((t) => `• ${t.name}: ${fmtDur(t.secs)}`).join("\n");
    return {
      pdf, s, e, who, count: dataDays.length,
      filename: `timeline_payperiod_${s}_to_${e}${focusName ? `_${slugify(focusName)}` : ""}.pdf`,
      subjectLabel: `Timeline pay-period PDF — ${who}`,
      summary,
      successMsg: `Pay-period PDF ready — ${who} (${dataDays.length} ${dataDays.length === 1 ? "day" : "days"}).`,
    };
  };

  // Team pack: cover + a section per cleaner (their active days), on one shared scale.
  const buildTeamPack = async (tid) => {
    const period = (await api.get(`/timesheets/period?date=${date}`)).data;
    const s = period.period_start, e = period.period_end;
    if (!s || !e) { toast.error("Could not load the pay period.", { id: tid }); return null; }
    const days = [];
    const [sy, sm, sd] = s.split("-").map(Number);
    const cur = new Date(Date.UTC(sy, sm - 1, sd));
    const [ey, em, ed] = e.split("-").map(Number);
    const endUTC = Date.UTC(ey, em - 1, ed);
    while (cur.getTime() <= endUTC) { days.push(cur.toISOString().slice(0, 10)); cur.setUTCDate(cur.getUTCDate() + 1); }

    const allMins = [];
    const totals = new Map();
    const activeByCleaner = new Map();
    const order = [];
    for (const day of days) {
      const dayData = await qc.ensureQueryData({ queryKey: ["timeline", day], queryFn: async () => (await api.get(`/timeline?date=${day}`)).data });
      (dayData.lanes || []).forEach((l) => {
        if (!(l.items || []).length) return;
        allMins.push(...dayMinsRange(day, [l]));
        const t = totals.get(l.cleaner_id) || { name: l.cleaner_name, secs: 0 };
        t.secs += l.worked_seconds || 0; totals.set(l.cleaner_id, t);
        if (!activeByCleaner.has(l.cleaner_id)) { activeByCleaner.set(l.cleaner_id, []); order.push(l.cleaner_id); }
        activeByCleaner.get(l.cleaner_id).push(day);
      });
    }
    if (!allMins.length) { toast.info("No cleaning activity in this pay period.", { id: tid }); return null; }

    const gStart = minToHm(Math.floor(Math.min(...allMins) / 30) * 30);
    const gEnd = minToHm(Math.ceil(Math.max(...allMins) / 30) * 30);
    setManualStart(gStart); setManualEnd(gEnd); setMode("manual");

    const totalPages = [...activeByCleaner.values()].reduce((a, d) => a + d.length, 0);
    setExportProgress({ current: 0, total: totalPages });

    const totalsList = [...totals.values()].sort((a, b) => a.name.localeCompare(b.name));
    const activeDayCount = new Set([].concat(...activeByCleaner.values())).size;
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [900, 640] });
    drawCoverPage(pdf, { subtitle: "Team pack — pay-period timeline", who: `${order.length} cleaner${order.length === 1 ? "" : "s"}`, s, e, dayCount: activeDayCount, gStart, gEnd, totals: totalsList });

    let done = 0;
    for (const cid of order) {
      setFocusCleaner(cid);
      const cName = totals.get(cid)?.name || "Cleaner";
      for (const day of activeByCleaner.get(cid)) {
        done += 1;
        setExportProgress({ current: done, total: totalPages });
        toast.loading(`Building team pack… ${done}/${totalPages}`, { id: tid });
        setDate(day);
        await new Promise((r) => setTimeout(r, 650));
        if (!captureRef.current) continue;
        const node = captureRef.current;
        const w = node.scrollWidth, h = node.scrollHeight;
        const dataUrl = await toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: w, height: h });
        pdf.addPage([w + 48, h + 96], "landscape");
        pdf.setFontSize(16); pdf.setTextColor(20);
        pdf.text(`${cName} — ${fmtDMY(day)}`, 24, 32);
        pdf.setFontSize(10); pdf.setTextColor(120);
        pdf.text(`KlenaPro shift review · scale ${gStart}–${gEnd}`, 24, 50);
        pdf.addImage(dataUrl, "PNG", 24, 72, w, h);
      }
    }
    const summary = `Team pack · Pay period: ${fmtDMY(s)} – ${fmtDMY(e)}\n\nWorked hours:\n` +
      totalsList.map((t) => `• ${t.name}: ${fmtDur(t.secs)}`).join("\n");
    return {
      pdf, s, e,
      filename: `timeline_teampack_${s}_to_${e}.pdf`,
      subjectLabel: "Timeline team pack",
      summary,
      successMsg: `Team pack ready — ${order.length} cleaner${order.length === 1 ? "" : "s"}, ${totalPages} page${totalPages === 1 ? "" : "s"}.`,
    };
  };

  // Runs a builder inside the exporting overlay, then performs `action` with the result, and restores state.
  const runPdfJob = async (builder, action, tid) => {
    exportingRef.current = true;
    setExporting(true);
    setExportProgress({ current: 0, total: 0 });
    toast.loading("Preparing…", { id: tid });
    const originalDate = date;
    const originalFocus = focusCleaner;
    try {
      const built = await builder(tid);
      if (!built) return;
      await action(built, tid);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.", { id: tid });
    } finally {
      exportingRef.current = false;
      setFocusCleaner(originalFocus);
      setDate(originalDate);
      setMode("auto");
      setExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  const exportPayPeriodPdf = () => runPdfJob(buildScopedPayPeriod, (b, tid) => { b.pdf.save(b.filename); toast.success(b.successMsg, { id: tid }); }, "payperiod-pdf");
  const exportTeamPackPdf = () => runPdfJob(buildTeamPack, (b, tid) => { b.pdf.save(b.filename); toast.success(b.successMsg, { id: tid }); }, "teampack-pdf");

  const emailToPayroll = () => runPdfJob(
    focusCleaner === "all" ? buildTeamPack : buildScopedPayPeriod,
    async (b, tid) => {
      toast.loading("Emailing to payroll…", { id: tid });
      const blob = b.pdf.output("blob");
      const fd = new FormData();
      fd.append("file", blob, b.filename);
      fd.append("filename", b.filename);
      fd.append("subject_label", b.subjectLabel);
      fd.append("body_summary", b.summary);
      fd.append("link_base", window.location.origin);
      if (emailRecipient.trim()) fd.append("recipient", emailRecipient.trim());
      const res = await api.post("/timeline/email-pdf", fd);
      toast.success(`Emailed to ${res.data.recipient}. A download link was sent.`, { id: tid });
    },
    "email-pdf",
  );
  const lanes = useMemo(() => data?.lanes || [], [data]);
  const cleanerOptions = lanes.map((l) => ({ id: l.cleaner_id, name: l.cleaner_name }));
  // Rooms worked this day (unique by clean_id) — used to recover a missed assist from a gap.
  const roomOptions = useMemo(() => {
    const map = new Map();
    lanes.forEach((l) => (l.items || []).forEach((it) => {
      if (it.clean_id && !map.has(it.clean_id)) map.set(it.clean_id, { clean_id: it.clean_id, label: it.label });
    }));
    return [...map.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label), undefined, { numeric: true }));
  }, [lanes]);
  const focusName = cleanerOptions.find((c) => c.id === focusCleaner)?.name || "";
  // Lanes actually shown/captured — all cleaners, or just the focused one.
  const displayLanes = focusCleaner === "all" ? lanes : lanes.filter((l) => l.cleaner_id === focusCleaner);

  // Minutes-of-day (relative to `date`) for a timeline item's ISO time.
  const isoMinOfDay = (iso) => {
    const s = isoToNZInput(iso); // YYYY-MM-DDTHH:mm
    const [d, t] = s.split("T");
    const [h, mm] = t.split(":").map(Number);
    let mins = h * 60 + mm;
    if (d > date) mins += 1440;
    else if (d < date) mins -= 1440;
    return mins;
  };
  const minToHm = (mins) => {
    const m = ((Math.round(mins) % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  };
  // Auto-fit window: floor/ceil the day's activity to the nearest half hour; default if empty.
  const fit = useMemo(() => {
    const mins = [];
    displayLanes.forEach((l) => l.items.forEach((it) => { mins.push(isoMinOfDay(it.start)); mins.push(isoMinOfDay(it.eff_end)); }));
    if (!mins.length) return { start: defStart, end: defEnd };
    return {
      start: minToHm(Math.floor(Math.min(...mins) / 30) * 30),
      end: minToHm(Math.ceil(Math.max(...mins) / 30) * 30),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayLanes, date, defStart, defEnd]);

  const appliedStart = mode === "auto" ? fit.start : manualStart;
  const appliedEnd = mode === "auto" ? fit.end : manualEnd;

  // Keep the input drafts in sync with the applied window (day change / auto-fit).
  useEffect(() => { setDraftStart(appliedStart); setDraftEnd(appliedEnd); }, [appliedStart, appliedEnd]);
  // Every time the day changes, fit the scale to that day's tasks — but keep the saved default
  // window on the very first load, and never override while exporting.
  useEffect(() => {
    if (firstLoad.current) { firstLoad.current = false; return; }
    if (!exportingRef.current) setMode("auto");
  }, [date]);

  const rangeDirty = draftStart !== appliedStart || draftEnd !== appliedEnd;
  const applyRange = () => { setMode("manual"); setManualStart(draftStart); setManualEnd(draftEnd); };
  const autoScale = () => setMode("auto");
  const saveDefault = async () => {
    setDefStart(draftStart);
    setDefEnd(draftEnd);
    localStorage.setItem("tl_default_start", draftStart);
    localStorage.setItem("tl_default_end", draftEnd);
    // Apply it right away (persists across reloads and is shared by all managers).
    setMode("manual");
    setManualStart(draftStart);
    setManualEnd(draftEnd);
    try {
      await api.put("/settings", { timeline_default_start: draftStart, timeline_default_end: draftEnd });
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success(`Team default set to ${draftStart}–${draftEnd}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the team default.");
    }
  };
  const hasData = displayLanes.some((l) => l.items.length > 0);

  // Team-wide projected finish — dynamic (today) or planned forecast (future days).
  const isFuture = date > todayNZ();
  const showPlan = (isToday || isFuture) && focusCleaner === "all";
  const expectedFinish = data?.expected_finish || null;
  // Orange live line: only meaningful for today (real, in-progress work).
  const finishMin =
    isToday && focusCleaner === "all" && expectedFinish?.finish
      ? isoMinOfDay(expectedFinish.finish)
      : null;
  const finishCaughtUp = finishMin != null && (expectedFinish?.remaining_minutes || 0) <= 0;
  // Grey planned line: shown for today and for future forecast days.
  const originalFinishMin =
    showPlan && expectedFinish?.original_finish
      ? isoMinOfDay(expectedFinish.original_finish)
      : null;
  const finishShade = expectedFinish?.shade || "none";
  const varianceMin = expectedFinish?.variance_minutes || 0;
  // Future-day forecast chip (no live work yet).
  const showForecast = isFuture && focusCleaner === "all" && originalFinishMin != null;

  return (
    <div className="space-y-5" data-testid="timeline-page">
      {exporting && exportProgress.total > 0 && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
          data-testid="timeline-export-overlay"
        >
          <div className="flex w-[min(90vw,380px)] flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 shadow-2xl">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
            <div className="text-center">
              <p className="font-display text-lg font-bold" data-testid="timeline-export-progress-text">
                Building page {exportProgress.current} of {exportProgress.total}…
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Assembling your PDF. This stays on until it's ready.</p>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-indigo-500 transition-all duration-300"
                style={{ width: `${Math.round((exportProgress.current / exportProgress.total) * 100)}%` }}
                data-testid="timeline-export-progress-bar"
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
            <GanttChartSquare className="h-7 w-7 text-indigo-500" /> Timeline
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Each cleaner's working day at a glance — tasks, breaks and the idle gaps in between. Click a bar for details or a gap to add a reason.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={focusCleaner} onValueChange={setFocusCleaner}>
            <SelectTrigger className="h-9 w-[190px] gap-2" data-testid="timeline-cleaner-filter">
              <Users className="h-4 w-4 text-muted-foreground" />
              <SelectValue placeholder="All cleaners" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="timeline-cleaner-all">All cleaners</SelectItem>
              {cleanerOptions.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`timeline-cleaner-${c.id}`}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportPdf} disabled={!hasData || exporting} data-testid="timeline-export-pdf" className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export PDF
          </Button>
          <Button variant="outline" onClick={exportPayPeriodPdf} disabled={exporting} data-testid="timeline-export-payperiod-pdf" className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            {focusCleaner === "all" ? "Pay-period PDF" : "Cleaner PDF"}
          </Button>
          {focusCleaner === "all" && (
            <Button variant="outline" onClick={exportTeamPackPdf} disabled={exporting} data-testid="timeline-export-teampack-pdf" className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
              Team pack
            </Button>
          )}
          <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={exporting} data-testid="timeline-email-payroll" className="gap-2">
            <Mail className="h-4 w-4" /> Email payroll
          </Button>
          <Button variant="outline" size="icon" onClick={() => shiftDay(-1)} data-testid="timeline-prev-day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold" data-testid="timeline-date">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            {fmtDMY(date)}
            {isToday && (
              <Badge className="ml-1 gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                <Radio className="h-3 w-3 animate-pulse" /> Live
              </Badge>
            )}
          </div>
          <Button variant="outline" size="icon" onClick={() => shiftDay(1)} data-testid="timeline-next-day">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button variant="outline" onClick={() => setDate(todayNZ())} data-testid="timeline-today">
              Today
            </Button>
          )}
        </div>
      </div>

      {/* legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="timeline-legend">
        {Object.entries(TIMELINE_KINDS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`inline-block h-3 w-3 rounded-sm ${v.dot}`} /> {v.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-amber-200 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(180,83,9,.5)_3px,rgba(180,83,9,.5)_5px)]" />
          Idle gap &gt; 15 min (tap to explain)
        </span>
      </div>

      {stuck.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3" data-testid="stuck-tasks-alert">
          <div className="flex items-center gap-2 font-display font-bold text-red-700">
            <AlarmClockOff className="h-5 w-5" /> {stuck.length} task{stuck.length > 1 ? "s" : ""} still marked "in progress"
          </div>
          <p className="mt-0.5 text-xs text-red-600/80">A cleaner clocked out, the task's been running 8h+, it was ended but never closed properly, or a helper is assisting a room that was never started. Force-end it so their hours are correct and they can start new work.</p>
          <div className="mt-2 space-y-1.5">
            {stuck.map((s) => (
              <div key={`${s.clean_id}-${s.cleaner_id}-${s.role}`} className="flex items-center justify-between gap-2 rounded-md bg-white px-3 py-2 text-sm" data-testid={`stuck-row-${s.clean_id}`}>
                <div className="min-w-0">
                  <div className="truncate font-semibold">{s.label} <span className="font-normal text-muted-foreground">· {s.cleaner_name}{s.role === "assist" ? " (assist)" : ""}</span></div>
                  <div className="text-xs text-muted-foreground">Running {fmtDur(s.running_seconds)} · {s.reason === "clocked_out" ? "clocked out" : s.reason === "incomplete_end" ? "didn't close properly" : s.reason === "orphan_assist" ? "assisting a room that was never started" : s.reason === "assist_after_finish" ? "still assisting 1h+ after the room was finished" : "over 8h"}{s.date !== date ? ` · ${fmtDMY(s.date)}` : ""}</div>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => forceEnd.mutate(s)}
                  disabled={forceEnd.isPending}
                  data-testid={`stuck-force-end-${s.clean_id}`}
                  className="shrink-0 gap-1"
                >
                  <AlarmClockOff className="h-4 w-4" /> Force end
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm" data-testid="timeline-range-controls">
            <span className="font-semibold text-muted-foreground">Show hours</span>
            <input
              type="time"
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && rangeDirty) applyRange(); }}
              data-testid="timeline-range-start"
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="time"
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && rangeDirty) applyRange(); }}
              data-testid="timeline-range-end"
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
            <Button size="sm" onClick={applyRange} disabled={!rangeDirty} data-testid="timeline-range-set" className="h-8">Set</Button>
            <Button size="sm" variant="outline" onClick={autoScale} disabled={mode === "auto"} data-testid="timeline-range-auto" className="h-8 gap-1">
              <GanttChartSquare className="h-3.5 w-3.5" /> Auto scale
            </Button>
            <Button size="sm" variant="ghost" onClick={saveDefault} data-testid="timeline-range-save-default" className="h-8 text-muted-foreground">
              Save as team default
            </Button>
            {mode === "auto" && <span className="text-xs text-muted-foreground">· fitted to this day's tasks</span>}
            {finishMin != null && (
              <span
                className="ml-auto flex items-center gap-1.5 rounded-md border border-orange-200 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700"
                data-testid="timeline-finish-summary"
              >
                <Clock className="h-3.5 w-3.5" />
                {finishCaughtUp
                  ? "All caught up — no work left"
                  : `Est. finish ~${minToHm(finishMin)} · ${expectedFinish.remaining_minutes} min left · ${expectedFinish.active_cleaners} active`}
                {!finishCaughtUp && finishShade !== "none" && (
                  <span
                    className={finishShade === "ahead" ? "text-emerald-700" : "text-red-700"}
                    data-testid="timeline-finish-variance"
                  >
                    · {Math.abs(varianceMin)} min {finishShade === "ahead" ? "ahead of" : "behind"} plan{originalFinishMin != null ? ` (${minToHm(originalFinishMin)})` : ""}
                  </span>
                )}
              </span>
            )}
            {showForecast && (
              <span
                className="ml-auto flex items-center gap-1.5 rounded-md border border-slate-300 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700"
                data-testid="timeline-forecast-summary"
              >
                <Clock className="h-3.5 w-3.5" />
                Planned finish ~{minToHm(originalFinishMin)} · {expectedFinish.remaining_total_minutes} min work · {expectedFinish.planned_cleaners} cleaner{expectedFinish.planned_cleaners === 1 ? "" : "s"} planned
              </span>
            )}
          </div>
          <TimelineTrack
            ref={captureRef}
            lanes={displayLanes}
            date={date}
            rangeStart={hmToMin(appliedStart)}
            rangeEnd={hmToMin(appliedEnd)}
            finishMin={finishCaughtUp ? null : finishMin}
            originalFinishMin={originalFinishMin}
            finishShade={finishCaughtUp ? "none" : finishShade}
            onItemClick={(it, lane) => { setEditing(false); setSelected({ ...it, cleaner_id: lane.cleaner_id, cleaner_name: lane.cleaner_name }); }}
            onGapClick={(gap, lane) => setGapCtx({ gap, lane })}
            onBreakClick={(b, lane) => setBreakEdit({ ...b, cleaner_id: lane.cleaner_id, cleaner_name: lane.cleaner_name })}
            onClockOut={(lane) => clockOut.mutate(lane.cleaner_id)}
            onClockOutUndo={(lane) => clockOutUndo.mutate(lane.cleaner_id)}
            clockOutPending={clockOut.isPending || clockOutUndo.isPending}
          />
        </>
      )}

      {/* gap reason dialog */}
      <GapReasonDialog
        open={!!gapCtx}
        onClose={() => setGapCtx(null)}
        gap={gapCtx?.gap}
        cleanerId={gapCtx?.lane?.cleaner_id}
        cleanerName={gapCtx?.lane?.cleaner_name}
        rooms={roomOptions}
        date={date}
        onSaved={() => qc.invalidateQueries({ queryKey: ["timeline", date] })}
      />

      {/* email-to-payroll dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="timeline-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-500" /> Email to payroll</DialogTitle>
            <DialogDescription>
              We'll build the {focusCleaner === "all" ? "team pack" : `${focusName} PDF`} for the current pay period and email a secure download link (valid 7 days).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="tl-email-recipient">Send to</Label>
            <Input
              id="tl-email-recipient"
              type="email"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              placeholder="payroll@yourmotel.co.nz"
              data-testid="timeline-email-recipient"
            />
            {!payrollSettings?.payroll_email && (
              <p className="text-xs text-muted-foreground">Tip: set a default payroll email in Payroll settings so this fills in automatically.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="timeline-email-cancel">Cancel</Button>
            <Button
              onClick={() => { setEmailOpen(false); emailToPayroll(); }}
              disabled={!emailRecipient.trim() || exporting}
              data-testid="timeline-email-send"
              className="gap-2"
            >
              <Mail className="h-4 w-4" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* item detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); } }}>
        <DialogContent data-testid="timeline-detail-dialog">
          {selected && !editing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className={`inline-block h-3 w-3 rounded-sm ${(TIMELINE_KINDS[selected.kind] || TIMELINE_KINDS.clean).dot}`} />
                  {selected.label}
                </DialogTitle>
                <DialogDescription>
                  {(TIMELINE_KINDS[selected.kind] || TIMELINE_KINDS.clean).label}
                  {selected.role === "assist" ? " · Assisting" : " · Primary"}
                  {selected.cleaner_name ? ` · ${selected.cleaner_name}` : ""}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Started" value={fmtTime(selected.start)} />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Finished</span>
                  <span className="font-semibold">
                    {selected.running ? (
                      <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        <Radio className="h-3 w-3 animate-pulse" /> In progress
                      </Badge>
                    ) : fmtTime(selected.end)}
                  </span>
                </div>
                <Row label="Time worked" value={fmtDur(selected.actual_seconds)} />
                <EstimateRow item={selected} date={date} onSaved={(v) => { setSelected({ ...selected, est_minutes: v }); qc.invalidateQueries({ queryKey: ["timeline", date] }); }} />
                {selected.auto_closed && (
                  <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700" data-testid="timeline-auto-closed-tag">
                    <AlarmClockOff className="h-4 w-4" /> Auto-closed by KlenaPro
                  </div>
                )}
                {selected.checklist_total > 0 && (
                  <Row label="Checklist" value={`${selected.checklist_done}/${selected.checklist_total} done`} />
                )}
                {selected.breaks.length > 0 && (
                  <Row label="Breaks / pauses" value={selected.breaks.length} />
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reported issues</span>
                  {selected.issues_open > 0 ? (
                    <span className="flex items-center gap-1 font-semibold text-amber-700">
                      <TriangleAlert className="h-4 w-4" /> {selected.issues_open} open
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 font-semibold text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" /> None
                    </span>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="timeline-edit-times-btn" className="gap-2">
                  <Pencil className="h-4 w-4" /> {selected.running ? "Edit / end times" : "Edit times"}
                </Button>
              </DialogFooter>
            </>
          )}
          {selected && editing && (
            <EditTimesForm
              item={selected}
              date={date}
              onCancel={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                setSelected(null);
                qc.invalidateQueries({ queryKey: ["timeline", date] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* edit-break dialog (focused: correct a single lunch/break with a reason) */}
      <Dialog open={!!breakEdit} onOpenChange={(o) => { if (!o) setBreakEdit(null); }}>
        <DialogContent data-testid="timeline-edit-break-dialog">
          {breakEdit && (
            <EditBreakForm
              brk={breakEdit}
              date={date}
              onCancel={() => setBreakEdit(null)}
              onSaved={() => { setBreakEdit(null); qc.invalidateQueries({ queryKey: ["timeline", date] }); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EstimateRow({ item, date, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(String(item.est_minutes ?? 0));
  const [reason, setReason] = useState("");
  const [updateDef, setUpdateDef] = useState(false);
  const [saving, setSaving] = useState(false);
  const actualMin = Math.round((item.actual_seconds || 0) / 60);
  const est = item.est_minutes || 0;
  const diff = item.running ? null : actualMin - est;
  const save = async () => {
    const n = Number(val);
    if (Number.isNaN(n) || n < 0) return toast.error("Enter minutes (0 or more)");
    setSaving(true);
    try {
      const r = await api.post(`/cleans/${item.clean_id}/estimate`, { est_minutes: n, reason, update_definition: updateDef });
      toast.success(r.data.definition_updated ? "Estimate updated — and saved as the task's standing estimate" : "Estimate updated for this day");
      setEdit(false); onSaved?.(n);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update estimate"); }
    finally { setSaving(false); }
  };
  if (!edit) {
    return (
      <div className="flex items-center justify-between" data-testid="timeline-est-row">
        <span className="text-muted-foreground">Estimated</span>
        <span className="flex items-center gap-2 font-semibold">
          {fmtMins(est)}
          {diff !== null && est > 0 && (
            <span className={`text-xs font-medium ${Math.abs(diff) > Math.max(5, est * 0.25) ? "text-amber-700" : "text-muted-foreground"}`} data-testid="timeline-est-variance">
              ({diff > 0 ? "+" : ""}{diff} min)
            </span>
          )}
          <button type="button" onClick={() => { setVal(String(est)); setEdit(true); }} className="text-muted-foreground hover:text-foreground" title="Edit estimate" data-testid="timeline-est-edit">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-md border bg-secondary/30 p-2" data-testid="timeline-est-form">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">Estimated</span>
        <Input type="number" min="0" step="1" value={val} onChange={(e) => setVal(e.target.value)} className="h-8 w-24" data-testid="timeline-est-input" />
        <span className="text-xs text-muted-foreground">min{!item.running ? ` · actual ${actualMin} min` : ""}</span>
        {!item.running && actualMin > 0 && (
          <button type="button" className="text-xs underline-offset-2 hover:underline" onClick={() => setVal(String(actualMin))} data-testid="timeline-est-use-actual">Use actual</button>
        )}
      </div>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional) — e.g. extra towels after full house" className="h-8 text-xs" data-testid="timeline-est-reason" />
      {item.is_task && (
        <label className="flex items-center gap-2 text-xs" data-testid="timeline-est-update-def">
          <Checkbox checked={updateDef} onCheckedChange={(v) => setUpdateDef(!!v)} /> Also make this the task's standing estimate for future days
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEdit(false)} data-testid="timeline-est-cancel">Cancel</Button>
        <Button size="sm" className="h-7 text-xs" disabled={saving} onClick={save} data-testid="timeline-est-save">{saving ? "Saving…" : "Save estimate"}</Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function EditTimesForm({ item, date, onCancel, onSaved }) {
  const [start, setStart] = useState(isoToNzHm(item.start));
  const [end, setEnd] = useState(isoToNzHm(item.end));
  const [reason, setReason] = useState("");
  const [breaks, setBreaks] = useState(() =>
    (item.breaks || []).map((b) => ({
      index: b.index, type: b.type,
      start: isoToNzHm(b.start), end: isoToNzHm(b.end),
      s0: isoToNzHm(b.start), e0: isoToNzHm(b.end),
    }))
  );
  const setBreakField = (i, field, val) =>
    setBreaks((p) => p.map((b, idx) => (idx === i ? { ...b, [field]: val } : b)));

  const save = useMutation({
    mutationFn: async () => {
      const startChanged = start !== isoToNzHm(item.start);
      const endChanged = end !== isoToNzHm(item.end);
      if (startChanged || endChanged) {
        await api.post(`/cleans/${item.clean_id}/edit-time`, {
          cleaner_id: item.cleaner_id,
          started_at: nzHmToIso(date, start),
          ended_at: end ? nzHmToIso(date, end) : "",
          reason: reason.trim(),
          session_started_at: item.start,
        });
      }
      for (const b of breaks) {
        if (b.start !== b.s0 || b.end !== b.e0) {
          await api.post(`/cleans/${item.clean_id}/edit-break`, {
            cleaner_id: item.cleaner_id,
            break_index: b.index,
            start: nzHmToIso(date, b.start),
            end: b.end ? nzHmToIso(date, b.end) : "",
            reason: reason.trim(),
            session_started_at: item.start,
          });
        }
      }
    },
    onSuccess: () => {
      toast.success(item.running && end ? "Times updated — task ended" : "Times updated");
      onSaved();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const anyChange =
    start !== isoToNzHm(item.start) ||
    end !== isoToNzHm(item.end) ||
    breaks.some((b) => b.start !== b.s0 || b.end !== b.e0);
  const canSave = !!start && !!reason.trim() && anyChange && !save.isPending;

  const forceEndNow = useMutation({
    mutationFn: () =>
      api.post(`/cleans/${item.clean_id}/force-end`, {
        cleaner_id: item.cleaner_id,
        reason: reason.trim() || "Force-ended from timeline",
      }),
    onSuccess: () => { toast.success("Task force-ended"); onSaved(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit times</DialogTitle>
        <DialogDescription>
          {item.label}{item.cleaner_name ? ` · ${item.cleaner_name}` : ""} · {item.role === "assist" ? "Assisting" : "Primary"}
          {item.running && <span className="mt-1 block text-xs text-amber-700">This task is still in progress. Setting a finish time will end it.</span>}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Start (NZ time)</Label>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="edit-time-start" />
          </div>
          <div className="space-y-1.5">
            <Label>Finish (NZ time)</Label>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="edit-time-end" placeholder="—" />
            {item.running && <span className="text-[11px] text-muted-foreground">Leave blank to keep it running.</span>}
          </div>
        </div>
        {breaks.length > 0 && (
          <div className="space-y-1.5 rounded-md border p-2">
            <Label className="text-xs">Breaks &amp; lunch (NZ time)</Label>
            {breaks.map((b, i) => (
              <div key={`${b.type}-${b.start || ""}-${i}`} className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
                <span className="text-xs font-medium capitalize text-muted-foreground">{b.type}</span>
                <Input type="time" value={b.start} onChange={(e) => setBreakField(i, "start", e.target.value)} className="h-8 w-28" data-testid={`edit-break-start-${i}`} />
                <Input type="time" value={b.end} onChange={(e) => setBreakField(i, "end", e.target.value)} className="h-8 w-28" data-testid={`edit-break-end-${i}`} />
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Reason <span className="text-destructive">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you changing this time? (kept on the audit trail)"
            maxLength={200}
            data-testid="edit-time-reason"
          />
        </div>
      </div>
      <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
        {item.running && (
          <Button
            variant="destructive"
            onClick={() => forceEndNow.mutate()}
            disabled={forceEndNow.isPending}
            data-testid="edit-time-force-end"
            className="w-full gap-2"
          >
            {forceEndNow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlarmClockOff className="h-4 w-4" />}
            End task now (works even if week is locked)
          </Button>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="edit-time-cancel">Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!canSave} data-testid="edit-time-save" className="gap-2">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save time edits
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}


function EditBreakForm({ brk, date, onCancel, onSaved }) {
  const [start, setStart] = useState(isoToNzHm(brk.start));
  const [end, setEnd] = useState(isoToNzHm(brk.end));
  const isLunch = brk.type === "lunch";
  const unfinished = !brk.end;
  const [reason, setReason] = useState(unfinished ? "Break was never ended — finish time set by manager" : "");

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        cleaner_id: brk.cleaner_id,
        break_index: brk.index,
        start: nzHmToIso(date, start),
        end: end ? nzHmToIso(date, end) : "",
        reason: reason.trim(),
      };
      if (brk.source === "task") {
        await api.post(`/cleans/${brk.clean_id}/edit-break`, { ...payload, session_started_at: brk.session_started_at });
      } else {
        await api.post(`/shift-break/edit`, { ...payload, date });
      }
    },
    onSuccess: () => { toast.success(`${isLunch ? "Lunch" : "Break"} time updated`); onSaved(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const changed = start !== isoToNzHm(brk.start) || end !== isoToNzHm(brk.end);
  const canSave = !!start && !!reason.trim() && changed && !save.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className={`inline-block h-3 w-3 rounded-full ${isLunch ? "bg-orange-500" : "bg-amber-500"}`} />
          Edit {isLunch ? "lunch" : "break"}
        </DialogTitle>
        <DialogDescription>
          {brk.cleaner_name}{brk.source === "shift" ? " · between tasks" : " · during a task"}
          {unfinished && <span className="mt-1 block text-xs font-semibold text-red-600">This {isLunch ? "lunch" : "break"} was never ended — set a finish time to correct it.</span>}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Start (NZ time)</Label>
            <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="edit-break-start" />
          </div>
          <div className="space-y-1.5">
            <Label>Finish (NZ time)</Label>
            <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="edit-break-end" placeholder="—" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Reason <span className="text-destructive">*</span></Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Cleaner forgot to end lunch (kept on the audit trail)"
            maxLength={200}
            data-testid="edit-break-reason"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid="edit-break-cancel">Cancel</Button>
        <Button onClick={() => save.mutate()} disabled={!canSave} data-testid="edit-break-save" className="gap-2">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
